import React from 'react';
import { useIntl } from 'react-intl';
import type { Music24Capabilities } from 'fym-common/src/license/music24Plans';
import { TypedMemo } from '../common/types';
import { planNameMessages } from '../music24/config/music24PlanNames';
import { SMLUpgradeFeature } from '../music24/config/music24PlanCapabilities';
import { formatPlanPrice, useDisplayCurrency } from '../music24/config/useDisplayCurrency';
import { extraArtistOfferFor } from '../music24/components/SMLUpgradeModal/extraArtistOffer';
import { useIsNativeEmbed } from '../music24/navigation/hooks/useIsNativeEmbed';
import { usePlanStore } from '../music24/components/SMLUpgradeModal/usePlanStore';
import type { Music24Plan } from '../music24/config/music24StripePlans';
/* eslint-disable css-modules/no-unused-class -- shared with ArtistSwitcherMenu.tsx; each file uses its own slice of it */
import styles from './ArtistSwitcherMenu.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ArtistSwitcherLimitBoxProps {
  current: Music24Capabilities;
  /** The cheapest plan that can hold more artists, from `useNextPlanForFeature`. `null` when the
   * caller already holds the top artist cap (Ultra). */
  nextPlan: Music24Plan | null;
  onUpgrade: () => void;
}

/** The `<a>` chunk: the next plan's name (or "Add an artist"), as the only clickable words in the box (design d2). */
function nextPlanLink(onUpgrade: () => void) {
  return (chunks: React.ReactNode[]) => (
    <button key="next-plan" type="button" className={styles.limLink} onClick={onUpgrade}>
      {chunks}
    </button>
  );
}

/**
 * Design d2: the tinted box under the disabled "Add artist" row, once the plan's artist limit is
 * reached. A paid plan below its cap offers one more artist ("Pro has 1 artist. Add an artist for
 * $31.20 a month"); else the box names the next plan that can hold more artists ("Free includes
 * 1 artist. On Plus, add more artists at 20 % off the plan price."). The plan comes from
 * `pickPlanForFeature`, the same function `LockedControl`'s chip reads.
 */
const ArtistSwitcherLimitBoxComponent: React.FC<ArtistSwitcherLimitBoxProps> = ({ current, nextPlan, onUpgrade }) => {
  const intl = useIntl();
  const currency = useDisplayCurrency();
  const isNativeEmbed = useIsNativeEmbed();
  const planStore = usePlanStore();
  // A paid plan below its cap buys one more artist at the plan price minus 20 % (plans rename
  // 2026-10-23). The native web view quotes no web price, and a store plan cannot buy an extra
  // artist, so both keep the next-plan line.
  const extraArtist = isNativeEmbed ? null : extraArtistOfferFor(SMLUpgradeFeature.artists, current, planStore);

  if (extraArtist !== null && current.artistsLimit !== null) {
    return (
      <div className={styles.lim} data-testid="artist-switcher-limit-box">
        {intl.formatMessage(
          {
            id: 'app.sml.artistSwitcher.limitBox.extraArtist',
            defaultMessage:
              '{plan} has {limit, plural, one {# artist} other {# artists}}. <a>Add an artist</a> for {price} a month, 20 % off the plan price.',
          },
          {
            plan: intl.formatMessage(planNameMessages[current.plan]),
            limit: current.artistsLimit,
            price: formatPlanPrice(intl, extraArtist.monthlyCents, currency),
            a: nextPlanLink(onUpgrade),
          }
        )}
      </div>
    );
  }

  if (nextPlan === null || current.artistsLimit === null) {
    return (
      <div className={styles.lim} data-testid="artist-switcher-limit-box">
        {intl.formatMessage({
          id: 'app.sml.artistSwitcher.limitBox.noUpgrade',
          defaultMessage: 'Every artist slot on your plan is in use. Contact support to add more artists.',
        })}
      </div>
    );
  }

  return (
    <div className={styles.lim} data-testid="artist-switcher-limit-box">
      {intl.formatMessage(
        {
          id: 'app.sml.artistSwitcher.limitBox.upgradeMore',
          defaultMessage:
            '{plan} includes {limit, plural, one {# artist} other {# artists}}. On <a>{nextPlan}</a>, add more artists at 20 % off the plan price.',
        },
        {
          plan: intl.formatMessage(planNameMessages[current.plan]),
          limit: current.artistsLimit,
          nextPlan: intl.formatMessage(planNameMessages[nextPlan.tier]),
          a: nextPlanLink(onUpgrade),
        }
      )}
    </div>
  );
};

export const ArtistSwitcherLimitBox = TypedMemo(ArtistSwitcherLimitBoxComponent);
